import {
  combineReducers,
  configureStore,
  Reducer,
  Store,
  ThunkDispatch,
} from '@reduxjs/toolkit'
import { createApi } from '@reduxjs/toolkit/query/react'
import bevarSlice from 'Modules/Boardwork/Bevar/shared/store/bevarSlice'
import bblArchiveSlice from 'Modules/Boardwork/DocumentArchive/BblArchive/BblArchiveSlice'
import boardArchiveSlice from 'Modules/Boardwork/DocumentArchive/BoardArchive/BoardArchiveSlice'
import uploadFilesSlice from 'Modules/Boardwork/DocumentArchive/UploadFIles/store/UploadFilesSlice'
import taskReducer from 'Modules/Boardwork/Tasks/shared/store'
import bankAccountsSlice from 'Modules/Economy/BankAccounts/store/BankAccountsSlice'
import budgetSlice from 'Modules/Economy/Budget/shared/store/budgetSlice'
import invoiceSlice from 'Modules/Economy/Invoice/shared/store/invoice.slice'
import loanSlice from 'Modules/Economy/Loan/shared/store/loanSlice'
import reimbursementSlice from 'Modules/Economy/Reimbursement/shared/reimbursementSlice'
import balanceAndTransactionsSlice from 'Modules/Economy/shared/balanceAndTransactionSlice'
import publicationSlice from 'Modules/HousingCooperative/Publications/shared/store/publicationSlice'
import { AnyAction } from 'redux'
import createSagaMiddleware, { Saga } from 'redux-saga'
import axiosBaseQuery from 'shared/utils/axiosBaseQuery'
import storeTags from '../constants/storeTags'
import bblApi from './bbl/bbl.service'
import { BoardReducer } from './board'
import clientsReducer from './clients'
import clientsApi from './clients/clients.service'
import CommunicationReducer from './communication'
import customtextApi from './customtexts/customtexts.service'
import economyApi from './economy/economy.service'
import EmailSettingsReducer from './emailSettings'
import { GuidesReducer } from './guides'
import { KeyFiguresReducer } from './keyFigures'
import menuReducer from './menu'
import menuApi from './menu/menu.service'
import modalReducer from './modal'
import { reducer as NotificationsReducer } from './notifications'
import { createSagaInjector, rootSaga } from './sagas'
import SendMessageReducer from './sendMessage'
import { SocketReducer } from './socket'
import terminologyApi from './terminology/terminology.service'
import { reducer as ToasterReducer } from './toaster'
import userApi from './user/user.service'
import boardUpdateListSlice from 'Modules/Boardwork/DocumentArchive/BoardArchive/UpdateFileList/BoardUpdateListSlice'
import prosjektArkivSlice from 'Modules/Boardwork/DocumentArchive/ProsjektArkiv/ProsjektArkivSlice'
import documentArchiveSlice from 'Modules/Boardwork/DocumentArchive/documentArchiveSlice'

/**
 * Store interface
 */
export interface PortalStore extends Store {
  injectReducer: (key: string, asyncReducer: Reducer) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  asyncReducers: any
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  injectSaga: (key: string, saga: Saga<any[]>) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  inject: (key: string, asyncReducer: Reducer, saga: Saga<any[]>) => void
  destroyReducer: (key: string) => void
}

// initialize an empty api service that we'll inject endpoints into later as needed
export const emptySplitApi = createApi({
  baseQuery: axiosBaseQuery({ baseUrl: '/' }),
  refetchOnFocus: true,
  tagTypes: storeTags,
  endpoints: () => ({}),
})

/**
 * Static reducers
 * Should exists over the whole application
 */
export let reducers = {
  board: BoardReducer,
  keyFigures: KeyFiguresReducer,
  guides: GuidesReducer,
  toaster: ToasterReducer,
  notifications: NotificationsReducer,
  socket: SocketReducer,
  sendMessage: SendMessageReducer,
  emailSettings: EmailSettingsReducer,
  communication: CommunicationReducer,
  tasks: taskReducer,
  // Slices
  [modalReducer.name]: modalReducer.reducer,
  [menuReducer.name]: menuReducer.reducer,
  [clientsReducer.name]: clientsReducer.reducer,
  [publicationSlice.name]: publicationSlice.reducer,
  [loanSlice.name]: loanSlice.reducer,
  [budgetSlice.name]: budgetSlice.reducer,
  [bevarSlice.name]: bevarSlice.reducer,
  [reimbursementSlice.name]: reimbursementSlice.reducer,
  [bankAccountsSlice.name]: bankAccountsSlice.reducer,
  [balanceAndTransactionsSlice.name]: balanceAndTransactionsSlice.reducer,
  [bblArchiveSlice.name]: bblArchiveSlice.reducer,
  [boardArchiveSlice.name]: boardArchiveSlice.reducer,
  [documentArchiveSlice.name]: documentArchiveSlice.reducer,
  [prosjektArkivSlice.name]: prosjektArkivSlice.reducer,
  [boardUpdateListSlice.name]: boardUpdateListSlice.reducer,
  [uploadFilesSlice.name]: uploadFilesSlice.reducer,
  [invoiceSlice.name]: invoiceSlice.reducer,
  // APIs
  [clientsApi.reducerPath]: clientsApi.reducer,
  [economyApi.reducerPath]: economyApi.reducer,
  [bblApi.reducerPath]: bblApi.reducer,
  [userApi.reducerPath]: userApi.reducer,
  [terminologyApi.reducerPath]: terminologyApi.reducer,
  [menuApi.reducerPath]: menuApi.reducer,
  [customtextApi.reducerPath]: customtextApi.reducer,
  [emptySplitApi.reducerPath]: emptySplitApi.reducer,
}

export const sagaMiddleware = createSagaMiddleware()
export const reduxMiddleware = [
  menuApi.middleware,
  clientsApi.middleware,
  bblApi.middleware,
  economyApi.middleware,
  userApi.middleware,
  customtextApi.middleware,
  terminologyApi.middleware,
  emptySplitApi.middleware,
]

export const store = configureStore({
  reducer: combineReducers(reducers),
  // @ts-expect-error: old code
  middleware: (getDefaultMiddleware) => [
    ...getDefaultMiddleware({ serializableCheck: false }).concat(
      reduxMiddleware
    ),
    sagaMiddleware,
  ],
  devTools: process.env.NODE_ENV !== 'production',
})

/**
 * Init store
 */

export const registerReducer = (key: string, newReducer: Reducer): Store => {
  reducers = { ...reducers, [key]: newReducer }
  // @ts-expect-error: old code
  store.replaceReducer(combineReducers(reducers))
  // @ts-expect-error: old code
  return store
}

export const injectSaga = createSagaInjector(sagaMiddleware.run, rootSaga)
export const injectReducer = registerReducer
export const inject = (
  key: string,
  asyncReducer?: Reducer,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  saga?: Saga<any[]>
) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  asyncReducer && injectReducer(key, asyncReducer)
  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
  saga && injectSaga(key, saga)
}

export type RootState = ReturnType<typeof store.getState>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AppDispatch = ThunkDispatch<RootState, any, AnyAction>

export default store
